import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/react-hooks';
import { gql } from 'apollo-boost';

import Loader from './Loader';
import { COMMUNITY_ADMIN_PATH } from '../../AdminDashboards/adminTools';

/*
 * Purchase review
 * ---------------
 * The buyer next to the users the purchase has to be reviewed against, one
 * user per column. That is the members of the saving groups that paid for the
 * purchase, or the accounts flagged as duplicates of the buyer. Every row is a
 * survey, so a survey several of them answered lines up across the columns:
 * when it was answered, how long every question took, and what was typed into
 * the open questions.
 */

const COMPARE = {
  SAVING_GROUP: 'SAVING_GROUP',
  DUPLICATES: 'DUPLICATES'
};

const GET_PURCHASE_REVIEW = gql`
  query getPurchaseReview(
    $userId: ID!
    $days: Int
    $limit: Int
    $compare: String
  ) {
    getPurchaseReview(
      userId: $userId
      days: $days
      limit: $limit
      compare: $compare
    ) {
      from
      to
      resultsLimit
      comparedWith
      savingGroups
      duplicateAccounts
      savingGroupDuplicates
      hasDuplicateAccountsFlag
      securityFlags {
        type
        flaggedOn
        expired
      }
      sharedSurveys
      purchases {
        id
        shopItemName
        tokenPrice
        createdAt
        inReview
      }
      users {
        id
        email
        nickname
        createdAt
        isBuyer
        relation
        securityFlags {
          type
          flaggedOn
          expired
        }
        resultsCount
        questionsCount
        medianSeconds
        fastAnswers
        openAnswers
      }
      surveys {
        surveyId
        surveyName
        sharedBy
        closestGapSeconds
        lastAnsweredAt
        cells {
          userId
          result {
            id
            startedAt
            complete
            campaignFailure
            notCounted
            deleted
            fromWebSurvey
            questionCount
            totalSeconds
            medianSeconds
            fastAnswers
            openAnswers
            questions {
              order
              seconds
              answerString
              questionText
            }
          }
        }
      }
    }
  }
`;

const DUPLICATE_ACCOUNTS = 'Duplicate Accounts';
const FAST_ANSWER_SECONDS = 2;

const flagStyle = flag => {
  if (flag.expired) return styles.flagExpired;
  if (flag.type === DUPLICATE_ACCOUNTS) return styles.flagDuplicate;
  return styles.flag;
};
// A bar of this many seconds fills the whole strip
const BAR_FULL_SECONDS = 30;
const BAR_HEIGHT = 26;

const formatDateTime = value => {
  if (!value) return '-';
  const date = new Date(value);
  return `${date.toLocaleDateString('nl-BE', {
    day: '2-digit',
    month: '2-digit'
  })} ${date.toLocaleTimeString('nl-BE', {
    hour: '2-digit',
    minute: '2-digit'
  })}`;
};

const formatDate = value =>
  value ? new Date(value).toLocaleDateString('nl-BE') : '-';

const formatDuration = seconds => {
  if (seconds === null || seconds === undefined) return '-';
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
};

const formatGap = seconds => {
  if (seconds === null || seconds === undefined) return null;
  if (seconds < 60) return `${seconds}s apart`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min apart`;
  return `${Math.round((seconds / 3600) * 10) / 10} h apart`;
};

// One bar per question, its height is the time spent on that question
function SecondsStrip({ questions }) {
  return (
    <div style={styles.strip}>
      {questions.map(question => {
        const seconds = question.seconds === null ? 0 : question.seconds;
        const height = Math.max(
          2,
          Math.round(
            (Math.min(seconds, BAR_FULL_SECONDS) / BAR_FULL_SECONDS) *
              BAR_HEIGHT
          )
        );
        const fast =
          question.seconds !== null && question.seconds < FAST_ANSWER_SECONDS;

        return (
          <span
            key={question.order}
            title={`#${question.order + 1} · ${
              question.seconds === null ? 'no time' : `${question.seconds}s`
            }${question.answerString ? ` · ${question.answerString}` : ''}`}
            style={{
              ...styles.bar,
              height: `${height}px`,
              background: fast ? '#eb6834' : '#2a78d6'
            }}
          />
        );
      })}
    </div>
  );
}

function QuestionLines({ questions }) {
  return (
    <div style={styles.questionLines}>
      {questions.map(question => (
        <div key={question.order} style={styles.questionLine}>
          <span style={styles.questionOrder}>#{question.order + 1}</span>
          <span
            style={{
              ...styles.questionSeconds,
              color:
                question.seconds !== null &&
                question.seconds < FAST_ANSWER_SECONDS
                  ? '#eb6834'
                  : '#555'
            }}
          >
            {question.seconds === null ? '-' : `${question.seconds}s`}
          </span>
          {question.answerString ? (
            <span style={styles.answer}>
              {question.questionText ? (
                <span style={styles.answerQuestion}>
                  {question.questionText}
                </span>
              ) : null}
              <span style={styles.answerText}>{question.answerString}</span>
            </span>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function ResultCell({ result, expanded, offsetSeconds }) {
  if (!result) {
    return <div style={styles.cellEmpty}>—</div>;
  }

  const flags = [
    result.complete ? null : 'not complete',
    result.campaignFailure ? 'screened out' : null,
    result.notCounted ? 'not counted' : null,
    result.deleted ? 'deleted' : null,
    result.fromWebSurvey ? 'web' : null
  ].filter(flag => flag);

  return (
    <div style={styles.cell}>
      <div style={styles.cellTime}>
        {formatDateTime(result.startedAt)}
        {offsetSeconds ? (
          <span style={styles.cellOffset}>
            +{formatDuration(offsetSeconds)}
          </span>
        ) : null}
      </div>
      <div style={styles.cellMeta}>
        {formatDuration(result.totalSeconds)} · med{' '}
        {result.medianSeconds === null ? '-' : `${result.medianSeconds}s`} ·{' '}
        {result.questionCount} q
      </div>
      {result.fastAnswers ? (
        <div style={styles.cellFast}>
          {result.fastAnswers} under {FAST_ANSWER_SECONDS}s
        </div>
      ) : null}
      {flags.length ? (
        <div style={styles.cellFlags}>{flags.join(' · ')}</div>
      ) : null}
      <SecondsStrip questions={result.questions} />
      {expanded ? <QuestionLines questions={result.questions} /> : null}
    </div>
  );
}

function PurchaseReview({ match }) {
  const { userId } = match.params;
  const [expandedRows, setExpandedRows] = useState({});
  const [compare, setCompare] = useState(COMPARE.SAVING_GROUP);

  const { loading, error, data } = useQuery(GET_PURCHASE_REVIEW, {
    variables: { userId, compare },
    fetchPolicy: 'network-only'
  });

  const review = data && data.getPurchaseReview;
  const users = (review && review.users) || [];
  const surveys = (review && review.surveys) || [];

  const toggleRow = surveyId =>
    setExpandedRows(rows => ({ ...rows, [surveyId]: !rows[surveyId] }));

  const columnWidth = `${Math.max(
    Math.floor(72 / Math.max(users.length, 1)),
    18
  )}%`;

  return (
    <div style={styles.container}>
      <Link
        to={`${COMMUNITY_ADMIN_PATH}/purchases-overview`}
        style={styles.backLink}
      >
        &larr; Back to purchases overview
      </Link>
      <h1>Purchase review</h1>
      <p style={styles.intro}>
        {compare === COMPARE.DUPLICATES
          ? `The buyer and the accounts that were flagged as duplicates of this
             one, side by side. Every row is a survey, so the surveys they have
             in common line up across the columns.`
          : `The buyer and the users that paid along through a saving group,
             side by side. Every row is a survey, so the surveys they have in
             common line up across the columns.`}
      </p>

      {error ? <p style={styles.error}>{error.message}</p> : null}
      {loading ? <Loader /> : null}

      {review ? (
        <>
          {review.securityFlags && review.securityFlags.length ? (
            <div style={styles.flags}>
              <span style={styles.flagsLabel}>Security flags</span>
              {review.securityFlags.map(flag => (
                <span key={flag.type} style={flagStyle(flag)}>
                  {flag.type}
                  {flag.flaggedOn ? ` · ${formatDate(flag.flaggedOn)}` : ''}
                  {flag.expired ? ' · expired' : ''}
                </span>
              ))}
            </div>
          ) : (
            <div style={styles.flags}>
              <span style={styles.flagsLabel}>Security flags</span>
              <span style={styles.flagNone}>none on this account</span>
            </div>
          )}

          {review.duplicateAccounts || compare === COMPARE.DUPLICATES ? (
            <div style={styles.compare}>
              <button
                type="button"
                style={
                  compare === COMPARE.SAVING_GROUP
                    ? styles.compareButtonActive
                    : styles.compareButton
                }
                onClick={() => setCompare(COMPARE.SAVING_GROUP)}
              >
                Saving group
              </button>
              <button
                type="button"
                style={
                  compare === COMPARE.DUPLICATES
                    ? styles.compareButtonActive
                    : styles.compareButton
                }
                onClick={() => setCompare(COMPARE.DUPLICATES)}
              >
                Review duplicate accounts ({review.duplicateAccounts})
              </button>
              {review.savingGroupDuplicates &&
              review.savingGroupDuplicates.length ? (
                <span style={styles.compareWarning}>
                  {review.savingGroupDuplicates.length === 1
                    ? '1 member of the saving group is also a duplicate account'
                    : `${
                        review.savingGroupDuplicates.length
                      } members of the saving group are also duplicate accounts`}
                  : {review.savingGroupDuplicates.join(', ')}
                </span>
              ) : null}
            </div>
          ) : null}

          <div style={styles.summary}>
            <span>
              <strong>Users:</strong> {users.length}
            </span>
            <span>
              <strong>
                {compare === COMPARE.DUPLICATES
                  ? 'Duplicate accounts:'
                  : 'Saving groups:'}
              </strong>{' '}
              {compare === COMPARE.DUPLICATES
                ? review.duplicateAccounts
                : review.savingGroups}
            </span>
            <span>
              <strong>Surveys in common:</strong> {review.sharedSurveys}
            </span>
            <span>
              <strong>Results per user:</strong> last {review.resultsLimit}
            </span>
          </div>

          {review.purchases && review.purchases.length ? (
            <div style={styles.purchases}>
              {review.purchases.map(purchase => (
                <span key={purchase.id} style={styles.purchase}>
                  <strong>{purchase.shopItemName}</strong>
                  {purchase.tokenPrice === null
                    ? ''
                    : ` · ${purchase.tokenPrice} tokens`}{' '}
                  · {formatDateTime(purchase.createdAt)}
                  {purchase.inReview ? (
                    <span style={styles.inReview}>in review</span>
                  ) : null}
                </span>
              ))}
            </div>
          ) : null}

          <div style={styles.table}>
            <div style={styles.headerRow}>
              <div style={styles.rowLabelHeader}>Survey</div>
              {users.map(user => (
                <div
                  key={user.id}
                  style={{ ...styles.userColumn, width: columnWidth }}
                >
                  <Link
                    to={`${COMMUNITY_ADMIN_PATH}/purchases-overview/${user.id}`}
                    style={styles.userEmail}
                  >
                    {user.email || user.nickname || user.id}
                  </Link>
                  <div style={styles.userBadges}>
                    {user.isBuyer ? (
                      <span style={styles.buyerBadge}>buyer</span>
                    ) : (
                      <span
                        style={
                          user.relation === 'DUPLICATE'
                            ? styles.duplicateBadge
                            : styles.memberBadge
                        }
                      >
                        {user.relation === 'DUPLICATE'
                          ? 'duplicate account'
                          : 'saving group'}
                      </span>
                    )}
                    {(user.securityFlags || [])
                      .filter(flag => !flag.expired)
                      .map(flag => (
                        <span key={flag.type} style={styles.columnFlag}>
                          {flag.type}
                        </span>
                      ))}
                    <span style={styles.userCreated}>
                      created {formatDate(user.createdAt)}
                    </span>
                  </div>
                  <div style={styles.userStats}>
                    {user.resultsCount} result
                    {user.resultsCount === 1 ? '' : 's'} · med{' '}
                    {user.medianSeconds === null
                      ? '-'
                      : `${user.medianSeconds}s`}
                    /q
                  </div>
                  <div style={styles.userStats}>
                    {user.fastAnswers} answer
                    {user.fastAnswers === 1 ? '' : 's'} under{' '}
                    {FAST_ANSWER_SECONDS}s · {user.openAnswers} typed
                  </div>
                </div>
              ))}
            </div>

            {surveys.map(row => {
              const shared = row.sharedBy > 1;
              const expanded = !!expandedRows[row.surveyId];
              // How much later than the first of them every user answered
              const startedAt = row.cells
                .filter(cell => cell.result && cell.result.startedAt)
                .map(cell => new Date(cell.result.startedAt).valueOf());
              const firstStartedAt = startedAt.length
                ? Math.min(...startedAt)
                : null;

              return (
                <div
                  key={row.surveyId}
                  style={{
                    ...styles.row,
                    ...(shared ? styles.rowShared : {})
                  }}
                >
                  <div style={styles.rowLabel}>
                    <button
                      type="button"
                      onClick={() => toggleRow(row.surveyId)}
                      style={styles.rowToggle}
                    >
                      {expanded ? '▾' : '▸'} {row.surveyName}
                    </button>
                    {shared ? (
                      <div style={styles.sharedBadge}>
                        answered by {row.sharedBy}
                        {row.closestGapSeconds !== null
                          ? ` · ${formatGap(row.closestGapSeconds)}`
                          : ''}
                      </div>
                    ) : null}
                  </div>
                  {row.cells.map(cell => (
                    <div
                      key={cell.userId}
                      style={{ ...styles.cellWrapper, width: columnWidth }}
                    >
                      <ResultCell
                        result={cell.result}
                        expanded={expanded}
                        offsetSeconds={
                          shared && cell.result && cell.result.startedAt
                            ? Math.round(
                                (new Date(cell.result.startedAt).valueOf() -
                                  firstStartedAt) /
                                  1000
                              )
                            : 0
                        }
                      />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>

          {!surveys.length ? (
            <p style={styles.empty}>These users have no results yet.</p>
          ) : null}

          <div style={styles.legend}>
            <span style={styles.legendItem}>
              <span style={{ ...styles.legendSwatch, background: '#2a78d6' }} />
              seconds spent on a question
            </span>
            <span style={styles.legendItem}>
              <span style={{ ...styles.legendSwatch, background: '#eb6834' }} />
              under {FAST_ANSWER_SECONDS} seconds
            </span>
            <span style={styles.legendItem}>
              Click a survey name to read the answers that were typed
            </span>
          </div>
        </>
      ) : null}
    </div>
  );
}

const styles = {
  container: {
    padding: '20px'
  },
  backLink: {
    display: 'inline-block',
    marginBottom: '10px',
    color: '#1f6fb2',
    textDecoration: 'none',
    fontWeight: 600
  },
  intro: {
    maxWidth: '760px',
    color: '#555'
  },
  error: {
    color: '#c0392b'
  },
  summary: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px 24px',
    padding: '12px 16px',
    marginBottom: '16px',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    background: '#fafafa',
    fontSize: '13px',
    color: '#444'
  },
  purchases: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '16px'
  },
  purchase: {
    padding: '6px 10px',
    borderRadius: '4px',
    background: '#fff4e2',
    color: '#8a4b00',
    fontSize: '12px'
  },
  inReview: {
    marginLeft: '6px',
    padding: '1px 6px',
    borderRadius: '10px',
    background: '#8a4b00',
    color: '#fff',
    fontSize: '11px',
    fontWeight: 700
  },
  table: {
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  headerRow: {
    display: 'flex',
    alignItems: 'stretch',
    background: '#f5f7fa',
    borderBottom: '1px solid #e0e0e0'
  },
  rowLabelHeader: {
    width: '28%',
    padding: '10px 12px',
    fontWeight: 700,
    fontSize: '13px',
    color: '#333'
  },
  userColumn: {
    padding: '10px 12px',
    borderLeft: '1px solid #e8e8e8'
  },
  userEmail: {
    display: 'block',
    color: '#1f6fb2',
    textDecoration: 'none',
    fontWeight: 600,
    fontSize: '13px',
    wordBreak: 'break-all'
  },
  userBadges: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    margin: '4px 0'
  },
  buyerBadge: {
    padding: '1px 6px',
    borderRadius: '10px',
    background: '#e3f1fb',
    color: '#1f6fb2',
    fontSize: '11px',
    fontWeight: 700
  },
  memberBadge: {
    padding: '1px 6px',
    borderRadius: '10px',
    background: '#f0f0f0',
    color: '#666',
    fontSize: '11px'
  },
  duplicateBadge: {
    padding: '1px 6px',
    borderRadius: '10px',
    background: '#fde8e8',
    color: '#a32020',
    fontSize: '11px',
    fontWeight: 700
  },
  columnFlag: {
    padding: '1px 6px',
    borderRadius: '10px',
    background: '#fdf1e2',
    color: '#8a4b00',
    fontSize: '10px'
  },
  flags: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '8px',
    marginBottom: '12px'
  },
  flagsLabel: {
    fontSize: '12px',
    color: '#666',
    fontWeight: 700
  },
  flag: {
    padding: '3px 9px',
    borderRadius: '12px',
    background: '#fdf1e2',
    color: '#8a4b00',
    fontSize: '12px'
  },
  flagDuplicate: {
    padding: '3px 9px',
    borderRadius: '12px',
    background: '#fde8e8',
    color: '#a32020',
    fontSize: '12px',
    fontWeight: 700
  },
  flagExpired: {
    padding: '3px 9px',
    borderRadius: '12px',
    background: '#f3f3f3',
    color: '#999',
    fontSize: '12px'
  },
  flagNone: {
    fontSize: '12px',
    color: '#888'
  },
  compare: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap'
  },
  compareButton: {
    padding: '7px 14px',
    borderRadius: '6px',
    border: '1px solid #d0d7de',
    background: '#ffffff',
    color: '#24292f',
    fontSize: '13px',
    cursor: 'pointer'
  },
  compareWarning: {
    alignSelf: 'center',
    color: '#a32020',
    fontSize: '12px',
    fontWeight: 600
  },
  compareButtonActive: {
    padding: '7px 14px',
    borderRadius: '6px',
    border: '1px solid #1f6fb2',
    background: '#1f6fb2',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 600,
    cursor: 'pointer'
  },
  userCreated: {
    fontSize: '11px',
    color: '#888'
  },
  userStats: {
    fontSize: '11px',
    color: '#666'
  },
  row: {
    display: 'flex',
    alignItems: 'stretch',
    borderBottom: '1px solid #eee'
  },
  rowShared: {
    background: '#f3f8fd'
  },
  rowLabel: {
    width: '28%',
    padding: '8px 12px'
  },
  rowToggle: {
    padding: 0,
    border: 'none',
    background: 'none',
    textAlign: 'left',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 600,
    color: '#173a56'
  },
  sharedBadge: {
    marginTop: '2px',
    fontSize: '11px',
    fontWeight: 700,
    color: '#1f6fb2'
  },
  cellWrapper: {
    borderLeft: '1px solid #f0f0f0',
    padding: '8px 12px'
  },
  cell: {
    fontSize: '12px',
    color: '#333'
  },
  cellEmpty: {
    fontSize: '12px',
    color: '#ccc'
  },
  cellTime: {
    fontWeight: 600
  },
  cellOffset: {
    marginLeft: '6px',
    color: '#eb6834',
    fontWeight: 600
  },
  cellMeta: {
    color: '#666',
    fontSize: '11px'
  },
  cellFast: {
    color: '#eb6834',
    fontSize: '11px'
  },
  cellFlags: {
    color: '#888',
    fontSize: '11px'
  },
  strip: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '1px',
    height: `${BAR_HEIGHT}px`,
    marginTop: '4px'
  },
  bar: {
    display: 'inline-block',
    width: '4px',
    borderRadius: '1px'
  },
  questionLines: {
    marginTop: '6px',
    borderTop: '1px solid #eee',
    paddingTop: '6px'
  },
  questionLine: {
    display: 'flex',
    gap: '6px',
    padding: '2px 0',
    fontSize: '11px',
    alignItems: 'baseline'
  },
  questionOrder: {
    color: '#aaa',
    minWidth: '26px'
  },
  questionSeconds: {
    minWidth: '30px',
    fontWeight: 600
  },
  answer: {
    display: 'block'
  },
  answerQuestion: {
    display: 'block',
    color: '#888'
  },
  answerText: {
    display: 'block',
    color: '#173a56',
    fontStyle: 'italic'
  },
  empty: {
    color: '#777'
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginTop: '10px',
    fontSize: '12px',
    color: '#777'
  },
  legendItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  legendSwatch: {
    display: 'inline-block',
    width: '10px',
    height: '10px',
    borderRadius: '2px'
  }
};

export default PurchaseReview;
